/**
 * SelectAsync — same look & behavior as <Select> (MUI Select dropdown), with two additions:
 *   1. Lazy load: fetches the next page when the dropdown is scrolled near the bottom
 *      (gated by `isFetchingNextPage` so a new request never fires mid-flight).
 *   2. Loading row rendered inside the dropdown popup while a page is loading.
 *
 * Server-driven options only (no enum). The already-selected value keeps its label via
 * `selectedOption` even when that id lives outside the currently loaded pages.
 *
 * Styling is intentionally kept identical to Select.tsx.
 */
import { IconClose, IconExpandMore } from "@/assets/icons";
import { useFormField } from "@/components/elements/FormField/FormField";
import { IconBox } from "@/components/elements/IconBox";
import { background, blue, button, divider, state, text } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { rounded } from "@/theme/variables";
import { TOption } from "@/types/common";
import { findLabelByValue } from "@/utils/object";
import {
  Box,
  CircularProgress,
  FormHelperText,
  MenuItem,
  Select as MuiSelect,
  SelectChangeEvent,
  SelectProps,
  Typography,
  css,
  styled,
} from "@mui/material";
import { FC, useMemo } from "react";
import { FetchNextPageOptions, InfiniteQueryObserverResult } from "@tanstack/react-query";
import { useNearBottomFetch } from "@/hooks/useNearBottomFetch";

export type TSelectAsyncSize = "md" | "lg";

export type TSelectAsyncProps = {
  options: TOption[];
  /** Pre-selected option (label-resolved) for a value that may live outside the loaded pages */
  selectedOption?: TOption | null;
  fetchNextPage: (options?: FetchNextPageOptions) => Promise<InfiniteQueryObserverResult | void>;
  hasNextPage?: boolean;
  /** True while any fetch runs (initial load) — drives the in-popup loading row when list is empty */
  isFetching?: boolean;
  /** True only while the NEXT page is loading — gates scroll-triggered fetches */
  isFetchingNextPage?: boolean;
  size?: TSelectAsyncSize;
  readonly?: boolean;
  clearable?: boolean;
  helperText?: string;
  nonControl?: boolean;
  disabled?: boolean;
  disabledValues?: string[];
  isShowIconComponent?: boolean;
  onChange?: (value: string) => void;
} & Omit<SelectProps, "size" | "children" | "readOnly" | "multiple" | "onChange">;

export const SelectAsync: FC<TSelectAsyncProps> = ({
  options,
  selectedOption,
  fetchNextPage,
  hasNextPage,
  isFetching = false,
  isFetchingNextPage = false,
  size = "md",
  readonly = false,
  nonControl = false,
  placeholder = "選択してください",
  clearable = true,
  helperText,
  disabled,
  disabledValues = [],
  isShowIconComponent = true,
  ...rest
}) => {
  const { field, fieldState } = useFormField(nonControl);
  const rawValue = field ? field.value : rest.value;
  // Convert 0 and null to "" since they are used as "no selection" but MUI Select expects ""
  const value = rawValue === 0 || rawValue === null ? "" : rawValue;

  // Merge the pre-selected option so its label resolves even when outside the loaded pages
  const selectOptions = useMemo(() => {
    if (selectedOption && !options.some((opt) => String(opt.value) === String(selectedOption.value))) {
      return [selectedOption, ...options];
    }
    return options;
  }, [options, selectedOption]);

  const selected = useMemo(() => {
    return [value].flat().flatMap((v) => {
      if (v === undefined || v === null || v === "") return [];
      const label = findLabelByValue(selectOptions, v);
      if (!label) return [];
      return label;
    });
  }, [value, selectOptions]);

  const IconComponent = useMemo(() => {
    if (!clearable) return;
    if (selected.length) {
      return () => (
        <IconBox
          sx={{ mr: 1 }}
          onClick={() => {
            rest.onChange?.("");
            field?.onChange(null);
            field?.onBlur();
          }}
        >
          {!readonly && !disabled && <IconClose fontSize={20} color={text.primary} />}
        </IconBox>
      );
    }
    return isFetching ? () => null : IconExpandMore;
  }, [isFetching, selected, readonly, disabled, clearable]);

  const onChange = (e: SelectChangeEvent<unknown>) => {
    rest.onChange?.(e.target.value as string);
    field?.onChange(e);
    field?.onBlur();
  };

  const handleMenuScroll = useNearBottomFetch({ hasNextPage, isFetchingNextPage, fetchNextPage });

  // Keep the loader at the bottom whenever more pages exist so it's visible the instant the user
  // reaches the end. No perpetual-spinner risk: hasNextPage ⟹ a full page is loaded ⟹ list scrolls.
  const showLoadingRow = Boolean(hasNextPage) || isFetchingNextPage || (isFetching && options.length === 0);

  return (
    <Box position="relative">
      <StyledSelect
        error={Boolean(fieldState?.error)}
        selectSize={size}
        readonly={readonly}
        displayEmpty
        IconComponent={isShowIconComponent ? IconComponent : undefined}
        {...rest}
        {...field}
        value={value ?? ""}
        disabled={disabled}
        onChange={onChange}
        MenuProps={{
          PaperProps: {
            sx: { maxWidth: 0 },
            onScroll: handleMenuScroll,
          },
        }}
        renderValue={() => {
          if (selected.length === 0) {
            return (
              <Typography variant="body14" color={`${text.tertiary}!important`}>
                {placeholder}
              </Typography>
            );
          }
          return selected.join(", ");
        }}
      >
        {selectOptions.map((option) => {
          const isDisable = "hidden" in option && option.hidden === true;
          const isHidden = isDisable && value !== option.value;
          return (
            !isHidden && (
              <MenuItem key={option.value} value={option.value} disabled={isDisable || disabledValues.includes(option?.value?.toString())} title={option.label}>
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>{option.label}</span>
              </MenuItem>
            )
          );
        })}
        {showLoadingRow && (
          <LoadingRow>
            <CircularProgress color="inherit" size={18} />
          </LoadingRow>
        )}
      </StyledSelect>
      {Boolean(helperText) && <FormHelperText>{helperText}</FormHelperText>}
    </Box>
  );
};

// Loading row inside the popup: just a centered spinner, no background (matches ComboboxMultiAsync)
const LoadingRow = styled("li")`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
  color: ${text.secondary};
  list-style: none;
`;

const styledOptions = { shouldForwardProp: (propName: string) => !["selectSize", "readonly"].includes(propName) };

const StyledSelect = styled(MuiSelect, styledOptions)<{ selectSize: TSelectAsyncSize; readonly: boolean }>`
  width: 100%;
  &.MuiInputBase-root {
    position: relative;
    background: ${background.white};
    .MuiSelect-select {
      color: ${text.primary};
    }
    &::placeholder {
      color: ${text.tertiary};
    }
    fieldset {
      border-color: ${divider.middle};
      border-radius: ${rounded.xs};
      border-width: 1px !important;
    }
    &:hover,
    &.Mui-focused {
      fieldset {
        border-color: ${blue[70]};
      }
    }
    &.Mui-focused {
      &::after {
        content: "";
        position: absolute;
        top: -4px;
        left: -4px;
        width: 100%;
        height: 100%;
        padding: 2px;
        border: 2px solid ${button.secondary};
        box-sizing: content-box;
        border-radius: ${rounded.sm};
        z-index: -1;
      }
    }

    &.Mui-error {
      background: ${background.error};
      fieldset {
        border-color: ${state.error_1}!important;
      }
    }
    &.Mui-disabled {
      background: ${background.disable};
      fieldset {
        border-color: ${divider.middle}!important;
      }
    }
    .MuiSelect-icon {
      transform: none !important;
      color: ${text.primary};
    }

    ${({ selectSize }) => (selectSize === "lg" ? LargeInput : MediumInput)};
    ${({ readonly }) => readonly && readonlyInput};
  }
`;

const readonlyInput = css`
  background: ${background.disable};
  pointer-events: none;
  .MuiSelect-select {
    color: ${text.primary} !important;
  }
  fieldset {
    border-color: ${divider.middle}!important;
  }
  &::placeholder {
    opacity: 0;
  }
`;

const MediumInput = css`
  .MuiInputBase-input {
    padding: 6px 8px;
    ${css(typography.body14)};
  }
`;

const LargeInput = css`
  .MuiInputBase-input {
    padding: 12px 8px;
    ${css(typography.body14)};
  }
`;
